import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import { usePlans } from "../../hooks/usePlans";
import { useScriptBoards } from "../../hooks/useScripts";
import { formatCommentTimestamp } from "../brief/briefUiUtils";
import { STATUS_META, bodyCellSx, cardSx, errorMessage, headCellSx, pageSx, primaryButtonSx } from "./scriptUi";

// /scripts — every plan that has a script board, with how far along it is.
// "New board" just picks a plan; the board itself is created on its first
// added creator (routes/scripts.js), so an abandoned pick leaves nothing behind.

const NewBoardDialog = ({ open, onClose, existingPlanIds }) => {
  const navigate = useNavigate();
  const { data, isFetching } = usePlans("active");
  const [plan, setPlan] = useState(null);
  const options = data?.plans ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Start script approval for a plan</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <Autocomplete
          options={options}
          loading={isFetching}
          value={plan}
          onChange={(_, v) => setPlan(v)}
          getOptionLabel={(p) => p?.name ?? ""}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, p) => (
            <li {...props} key={p.id}>
              <Box>
                <Typography sx={{ fontSize: 14 }}>{p.name}</Typography>
                <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                  {[p.brand, `${p.influencerCount ?? 0} creators`, existingPlanIds.has(p.id) && "already has a board"]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => <TextField {...params} autoFocus placeholder="Choose a plan" />}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" disabled={!plan} onClick={() => navigate(`/scripts/${plan.id}`)} sx={primaryButtonSx}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProgressCell = ({ board }) => {
  const pct = board.total ? Math.round((board.counts.approved / board.total) * 100) : 0;
  return (
    <Box sx={{ minWidth: 160 }}>
      <Typography sx={{ fontSize: 13, mb: 0.5 }}>
        {board.counts.approved}/{board.total} approved
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 6, borderRadius: 3, bgcolor: "#F3F4F6", "& .MuiLinearProgress-bar": { bgcolor: STATUS_META.approved.dot } }}
      />
    </Box>
  );
};

const NeedsAttentionCell = ({ counts }) => {
  const parts = [
    counts.changes_requested && { n: counts.changes_requested, status: "changes_requested" },
    counts.in_review && { n: counts.in_review, status: "in_review" },
    counts.not_started && { n: counts.not_started, status: "not_started" },
  ].filter(Boolean);
  if (!parts.length) return <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>—</Typography>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {parts.map(({ n, status }) => (
        <Typography key={status} sx={{ fontSize: 12.5, color: STATUS_META[status].color, fontWeight: 600 }}>
          {n} {STATUS_META[status].label.toLowerCase()}
        </Typography>
      ))}
    </Box>
  );
};

const ScriptBoards = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useScriptBoards();
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const boards = useMemo(() => data?.boards ?? [], [data]);
  const existingPlanIds = useMemo(() => new Set(boards.map((b) => b.planId)), [boards]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? boards.filter((b) => b.planName.toLowerCase().includes(q) || (b.brandName || "").toLowerCase().includes(q))
      : boards;
  }, [boards, search]);

  return (
    <>
      <DashboardHeaderSection hideSearch />
      <Box sx={pageSx}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Script Approval</Typography>
            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
              Share scripts with creators and brands, collect their edits and sign-off in one place.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)} sx={primaryButtonSx}>
            New board
          </Button>
        </Box>

        <Box sx={cardSx}>
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              placeholder="Search plan or brand"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: { xs: "100%", sm: 280 }, "& .MuiInputBase-root": { borderRadius: "10px" } }}
            />
          </Box>
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, minWidth: 220 }}>Plan</TableCell>
                  <TableCell sx={headCellSx}>Creators</TableCell>
                  <TableCell sx={headCellSx}>Progress</TableCell>
                  <TableCell sx={headCellSx}>Needs attention</TableCell>
                  <TableCell sx={headCellSx}>Last activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8, border: 0 }}>
                      <CircularProgress size={28} sx={{ color: "#FF5200" }} />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8, border: 0, color: "#B91C1C" }}>
                      {errorMessage(error, "Couldn't load script boards.")}
                    </TableCell>
                  </TableRow>
                ) : visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8, border: 0 }}>
                      <Typography sx={{ fontWeight: 600, color: "text.secondary" }}>
                        {boards.length === 0 ? "No script boards yet" : "No boards match"}
                      </Typography>
                      {boards.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Pick a plan with "New board", then add the creators the brand has approved.
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((board) => (
                    <TableRow key={board.planId} hover onClick={() => navigate(`/scripts/${board.planId}`)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={bodyCellSx}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{board.planName}</Typography>
                        <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                          {board.brandName || "No brand"}
                          {board.isArchived && " · archived plan"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>{board.total}</TableCell>
                      <TableCell sx={bodyCellSx}><ProgressCell board={board} /></TableCell>
                      <TableCell sx={bodyCellSx}><NeedsAttentionCell counts={board.counts} /></TableCell>
                      <TableCell sx={bodyCellSx}>{formatCommentTimestamp(board.lastActivityAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Box>

      {newOpen && <NewBoardDialog open onClose={() => setNewOpen(false)} existingPlanIds={existingPlanIds} />}
    </>
  );
};

export default ScriptBoards;
